import React, { useState } from 'react';
import { AlertTriangle, Copy } from 'lucide-react';
import { Button } from './Button.jsx';
import { cn } from '../lib/utils.js';

export function cx(...classes) {
  return classes.filter(Boolean).join(' ');
}

const badgeTones = {
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300',
  amber: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300',
  blue: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-300',
  rose: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300',
  slate: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300',
};

export function StatusBadge({ tone = 'slate', children }) {
  return <span className={cx('inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold uppercase tracking-wide', badgeTones[tone] || badgeTones.slate)}>{children}</span>;
}

export function MetricCard({ label, value, detail, icon: Icon, tone = 'blue' }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300',
    emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300',
    rose: 'bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300',
    slate: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  };
  return (
    <article className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</p>
          <p className="mt-2 text-3xl font-black tracking-tight text-slate-950 dark:text-white">{value}</p>
        </div>
        {Icon ? <div className={cx('flex h-11 w-11 items-center justify-center rounded-2xl', colors[tone] || colors.blue)}><Icon className="h-5 w-5" /></div> : null}
      </div>
      {detail ? <p className="mt-4 text-sm leading-6 text-slate-500 dark:text-slate-400">{detail}</p> : null}
    </article>
  );
}

export function SectionCard({ eyebrow, title, description, children, action }) {
  return (
    <section className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          {eyebrow ? <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600 dark:text-blue-400">{eyebrow}</p> : null}
          <h2 className="mt-1 text-2xl font-black tracking-tight text-slate-950 dark:text-white">{title}</h2>
          {description ? <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500 dark:text-slate-400">{description}</p> : null}
        </div>
        {action}
      </div>
      {children ? <div className="mt-5">{children}</div> : null}
    </section>
  );
}

function isCopyableCell(value) {
  return typeof value === 'string' && value.length > 16 && /(pi_|mrc_|loc_|plt_|wh_|whd_|evt_|req_|approval_|drift_|[a-f0-9]{16,}|https?:\/\/)/i.test(value);
}

function renderTableCell(value) {
  if (isCopyableCell(value)) return <CopyableValue value={value} compact />;
  return value;
}

export function CopyableValue({ value, label = 'Copy', compact = false }) {
  const [copied, setCopied] = useState(false);
  const display = compact && typeof value === 'string' && value.length > 28 ? `${value.slice(0, 14)}…${value.slice(-8)}` : value;
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(String(value));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  };
  return (
    <span className="inline-flex max-w-full items-center gap-2 rounded-xl bg-slate-50 px-2.5 py-1 font-mono text-xs text-slate-700 dark:bg-slate-950 dark:text-slate-300">
      <span className="min-w-0 truncate" title={String(value)}>{display}</span>
      <button type="button" onClick={copy} className="shrink-0 rounded-lg p-1 text-slate-400 transition hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-white" aria-label={label}>
        <Copy className="h-3.5 w-3.5" />
      </button>
      {copied ? <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-300">Copied</span> : null}
    </span>
  );
}

export function DataTable({ columns, rows }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-left text-sm dark:divide-slate-800">
          <thead className="bg-slate-50 text-xs font-black uppercase tracking-wide text-slate-500 dark:bg-slate-950 dark:text-slate-400">
            <tr>{columns.map((column) => <th key={column} className="whitespace-nowrap px-4 py-3 sm:px-5">{column}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map((row, index) => (
              <tr key={row.id || row.name || row.label || row.provider || index} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/60">
                {columns.map((column) => <td key={column} className="max-w-[18rem] px-4 py-4 align-top text-slate-700 dark:text-slate-300 sm:px-5">{renderTableCell(row[column.toLowerCase()])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function BlockerRow({ icon: Icon = AlertTriangle, title, body, tone = 'blue' }) {
  const color = tone === 'amber'
    ? 'text-amber-700 bg-amber-50 dark:bg-amber-950/50 dark:text-amber-300'
    : tone === 'emerald'
      ? 'text-emerald-700 bg-emerald-50 dark:bg-emerald-950/50 dark:text-emerald-300'
      : tone === 'rose'
        ? 'text-rose-700 bg-rose-50 dark:bg-rose-950/50 dark:text-rose-300'
        : 'text-blue-700 bg-blue-50 dark:bg-blue-950/50 dark:text-blue-300';
  return (
    <div className="flex gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className={cx('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', color)}><Icon className="h-5 w-5" /></div>
      <div><p className="font-bold text-slate-950 dark:text-white">{title}</p><p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">{body}</p></div>
    </div>
  );
}

export function EmptyState({ title, body, action }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm dark:border-slate-700 dark:bg-slate-950">
      <p className="font-bold text-slate-950 dark:text-white">{title}</p>
      {body ? <p className="mt-2 leading-6 text-slate-500 dark:text-slate-400">{body}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function AdminKeyPanel({ adminKey, setAdminKey, onLoad, loading, error }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
        <div>
          <p className="text-sm font-bold text-slate-950 dark:text-white">Connect platform data</p>
          <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">Enter the admin key locally to load sandbox processor data. Stored values are not displayed back.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            value={adminKey}
            onChange={(event) => setAdminKey(event.target.value)}
            className="h-10 min-w-64 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 dark:border-slate-800 dark:bg-slate-950 dark:text-white"
            placeholder="x-atlas-admin-key"
            aria-label="Admin key"
            type="password"
          />
          <Button onClick={onLoad} disabled={loading} type="button">{loading ? 'Loading…' : 'Load data'}</Button>
        </div>
      </div>
      {error ? <p className="mt-3 text-sm font-medium text-rose-600 dark:text-rose-300">{error}</p> : null}
    </div>
  );
}

export function SkeletonScreen({ eyebrow, title, description, cards = [], children }) {
  return (
    <div className="grid gap-5">
      <SectionCard eyebrow={eyebrow} title={title} description={description}>
        {children || <EmptyState title="Screen foundation ready" body="This route is now part of the Atlas platform shell. The next pass wires the existing backend endpoints into production-grade UX panels." />}
      </SectionCard>
      {cards.length ? <section className="grid gap-4 lg:grid-cols-3">{cards.map((card) => <MetricCard key={card.label} {...card} />)}</section> : null}
    </div>
  );
}

export const surfaceClass = 'rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900';
export const mutedTextClass = 'text-slate-500 dark:text-slate-400';
export const strongTextClass = 'text-slate-950 dark:text-white';
export const labelClass = 'text-sm font-bold text-slate-700 dark:text-slate-300';
export const inputClass = cn('h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 font-normal text-slate-900 dark:border-slate-800 dark:bg-slate-950 dark:text-white');
